import React, { useState, useEffect } from 'react';

const ResizableTable = ({ 
    columns, 
    data, 
    onRowClick,
    initialColumnWidths = {},
    className = '',
    striped = true,
    hover = true,
    darkTheme = false,
    tableId,
    minHeight = '350px',
    getRowClassName = null,  // Function to get custom row className
    enableSorting = false,   // Enable/disable sorting functionality
    onSort = null,          // Callback when sort changes (sortColumn, sortDirection)
    sortColumn = null,      // Current sort column
    sortDirection = null    // Current sort direction ('asc' | 'desc')
}) => {
    // Convert initial percentage widths to pixel values
    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem(`table_widths_${tableId}`);
        if (savedWidths) {
            return JSON.parse(savedWidths);
        }
        const widths = { ...initialColumnWidths };
        Object.entries(widths).forEach(([key, value]) => {
            if (typeof value === 'string' && (value.endsWith('%') || value === 'auto')) {
                // Keep percentage values and 'auto' as is
                widths[key] = value;
            }
        });
        return widths;
    });

    // Add state for column order
    const [columnOrder, setColumnOrder] = useState(() => {
        const savedOrder = localStorage.getItem(`table_column_order_${tableId}`);
        if (savedOrder) {
            const saved = JSON.parse(savedOrder);
            // Ensure saved order includes all current columns
            const currentColumnIds = columns.map(col => col.id);
            const missingColumns = currentColumnIds.filter(id => !saved.includes(id));
            const obsoleteColumns = saved.filter(id => !currentColumnIds.includes(id));
            
            if (missingColumns.length > 0 || obsoleteColumns.length > 0) {
                // Return current columns order if there's a mismatch
                return currentColumnIds;
            }
            return saved;
        }
        return columns.map(col => col.id);
    });

    // Add state for drag and drop
    const [draggedColumn, setDraggedColumn] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);

    const [tableWidth, setTableWidth] = useState(() => {
        const savedTableWidth = localStorage.getItem(`table_total_width_${tableId}`);
        return savedTableWidth || '100%';
    });

    const [resizing, setResizing] = useState(null);

    // Handle sorting
    const handleSort = (columnId, sortable) => {
        if (!enableSorting || !sortable || !onSort) return;
        
        let newDirection = 'asc';
        if (sortColumn === columnId && sortDirection === 'asc') {
            newDirection = 'desc';
        }
        
        onSort(columnId, newDirection);
    };

    // Function to render sort indicator
    const renderSortIndicator = (columnId, sortable) => {
        if (!enableSorting || !sortable) return null;
        
        const isActive = sortColumn === columnId;
        const direction = sortDirection;
        
        return (
            <span style={{ 
                marginLeft: '6px', 
                fontSize: '14px', 
                color: isActive ? '#007bff' : '#6c757d',
                fontWeight: 'bold'
            }}>
                {!isActive && '⇅'}
                {isActive && direction === 'asc' && '↑'}
                {isActive && direction === 'desc' && '↓'}
            </span>
        );
    };

    useEffect(() => {
        if (tableId) {
            localStorage.setItem(`table_widths_${tableId}`, JSON.stringify(columnWidths));
        }
    }, [columnWidths, tableId]);

    useEffect(() => {
        if (tableId) {
            localStorage.setItem(`table_total_width_${tableId}`, tableWidth);
        }
    }, [tableWidth, tableId]);

    useEffect(() => {
        if (tableId) {
            localStorage.setItem(`table_column_order_${tableId}`, JSON.stringify(columnOrder));
        }
    }, [columnOrder, tableId]);

    // Sync columnOrder when columns prop changes
    useEffect(() => {
        const currentColumnIds = columns.map(col => col.id);
        const missingColumns = currentColumnIds.filter(id => !columnOrder.includes(id));
        const obsoleteColumns = columnOrder.filter(id => !currentColumnIds.includes(id));
        
        if (missingColumns.length > 0 || obsoleteColumns.length > 0) {
            // Preserve existing order for columns that still exist, add new ones at the end
            const newOrder = columnOrder.filter(id => currentColumnIds.includes(id));
            const newColumns = currentColumnIds.filter(id => !newOrder.includes(id));
            setColumnOrder([...newOrder, ...newColumns]);
        }
    }, [columns, columnOrder]);

    const startResizing = (columnId, nextColumnId, e) => {
        e.preventDefault();
        const currentWidth = columnWidths[columnId];
        
        // Convert widths to pixels if they're percentages or auto
        let startWidth = currentWidth;
        let nextStartWidth = 0;

        // Get the table element and its width safely
        const tableElement = e.currentTarget.closest('table');
        const tableWidth = tableElement ? tableElement.offsetWidth : 0;
        const currentElement = e.currentTarget;

        if (typeof currentWidth === 'string') {
            if (currentWidth.endsWith('%')) {
                startWidth = tableWidth ? (parseFloat(currentWidth) / 100) * tableWidth : 100;
            } else if (currentWidth === 'auto') {
                startWidth = currentElement ? currentElement.parentElement.offsetWidth : 100;
            }
        }

        // If there's a next column, get its width
        if (nextColumnId) {
            const nextWidth = columnWidths[nextColumnId];
            const nextElement = currentElement.parentElement.nextElementSibling;

            if (typeof nextWidth === 'string') {
                if (nextWidth.endsWith('%')) {
                    nextStartWidth = tableWidth ? (parseFloat(nextWidth) / 100) * tableWidth : 100;
                } else if (nextWidth === 'auto') {
                    nextStartWidth = nextElement ? nextElement.offsetWidth : 100;
                }
            } else {
                nextStartWidth = nextWidth || 100;
            }
        }

        setResizing({
            columnId,
            nextColumnId,
            startX: e.pageX,
            startWidth: typeof startWidth === 'string' ? parseFloat(startWidth) : startWidth,
            nextStartWidth,
            isLastColumn: !nextColumnId,
            tableStartWidth: tableElement ? tableElement.offsetWidth : 0
        });
    };

    const stopResizing = () => {
        setResizing(null);
    };

    const handleMouseMove = (e) => {
        if (resizing) {
            const diff = e.pageX - resizing.startX;
            const newWidth = Math.max(50, resizing.startWidth + diff);

            if (resizing.isLastColumn) {
                // For the last column, adjust the table width
                const newTableWidth = Math.max(resizing.tableStartWidth, resizing.tableStartWidth + diff);
                setTableWidth(`${newTableWidth}px`);
                setColumnWidths(prev => ({
                    ...prev,
                    [resizing.columnId]: newWidth
                }));
            } else {
                // For other columns, adjust both current and next column
                const newNextWidth = Math.max(50, resizing.nextStartWidth - diff);
                setColumnWidths(prev => ({
                    ...prev,
                    [resizing.columnId]: newWidth,
                    [resizing.nextColumnId]: newNextWidth
                }));
            }
        }
    };

    useEffect(() => {
        if (resizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', stopResizing);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', stopResizing);
            };
        }
    }, [resizing]);

    const handleDragStart = (e, columnId) => {
        setDraggedColumn(columnId);
        e.dataTransfer.effectAllowed = 'move';
        // Add some transparency to dragged element
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        setDraggedColumn(null);
        setDragOverColumn(null);
        e.target.style.opacity = '1';
    };

    const handleDragOver = (e, columnId) => {
        e.preventDefault();
        if (columnId !== draggedColumn) {
            setDragOverColumn(columnId);
        }
    };

    const handleDrop = (e, targetColumnId) => {
        e.preventDefault();
        
        if (draggedColumn === targetColumnId) return;

        const newColumnOrder = [...columnOrder];
        const draggedIdx = newColumnOrder.indexOf(draggedColumn);
        const targetIdx = newColumnOrder.indexOf(targetColumnId);

        // Validation: Check if both columns exist in the order
        if (draggedIdx === -1 || targetIdx === -1) {
            return;
        }

        // Handle different drag directions properly
        if (draggedIdx > targetIdx) {
            // Dragging from right to left
            newColumnOrder.splice(draggedIdx, 1);
            newColumnOrder.splice(targetIdx, 0, draggedColumn);
        } else {
            // Dragging from left to right
            if (targetIdx === newColumnOrder.length - 1) {
                // Special case: dragging to the last position
                newColumnOrder.splice(draggedIdx, 1);
                newColumnOrder.push(draggedColumn);
            } else {
                // Normal left-to-right drag
                newColumnOrder.splice(draggedIdx, 1);
                newColumnOrder.splice(targetIdx - 1, 0, draggedColumn);
            }
        }
        setColumnOrder(newColumnOrder);
        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    // Sort columns based on columnOrder
    const orderedColumns = columns.slice().sort((a, b) => {
        return columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id);
    });

    const tableClasses = [
        'table',
        striped && 'table-striped',
        hover && 'table-hover',
        className
    ].filter(Boolean).join(' ');

    const getColumnWidth = (id) => {
        const width = columnWidths[id];
        if (width === 'auto') {
            return undefined; // Let the browser handle auto width
        }
        if (typeof width === 'string' && width.endsWith('%')) {
            return width; // Return percentage value as is
        }
        return typeof width === 'number' ? `${width}px` : width; // Convert numbers to pixel values
    };

    return (
        <div className="table-container" style={{ 
            position: 'relative',
            minWidth: tableWidth === '100%' ? '100%' : tableWidth,
            paddingRight: tableWidth === '100%' ? 0 : '20px',
            minHeight: minHeight,
            overflow: 'visible'
        }}>
            <div style={{ overflow: 'visible', height: '100%' }}>
                <table className={tableClasses} style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead className={darkTheme ? 'table-dark' : ''} style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: darkTheme ? '#212529' : '#fff' }}>
                        <tr>
                            {orderedColumns.map(({ id, header, sortable }, index) => (
                                <th
                                    key={id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, id)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, id)}
                                    onDrop={(e) => handleDrop(e, id)}
                                    style={{
                                        width: getColumnWidth(id),
                                        position: 'relative',
                                        padding: '0.5rem',
                                        border: '1px solid var(--bs-table-border-color)',
                                        cursor: 'grab',
                                        backgroundColor: dragOverColumn === id ? 'rgba(0, 0, 0, 0.1)' : undefined
                                    }}
                                >
                                    <div 
                                        role={enableSorting && sortable ? 'button' : undefined}
                                        tabIndex={enableSorting && sortable ? 0 : undefined}
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            cursor: enableSorting && sortable ? 'pointer' : 'inherit',
                                            padding: enableSorting && sortable ? '2px 4px' : '0',
                                            borderRadius: enableSorting && sortable ? '3px' : '0',
                                            userSelect: 'none',
                                            backgroundColor: enableSorting && sortable && sortColumn === id ? 'rgba(0, 123, 255, 0.1)' : 'transparent'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSort(id, sortable);
                                        }}
                                        onKeyDown={(e) => {
                                            if (enableSorting && sortable && (e.key === 'Enter' || e.key === ' ')) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSort(id, sortable);
                                            }
                                        }}
                                        onMouseEnter={(e) => {
                                            if (enableSorting && sortable) {
                                                e.target.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (enableSorting && sortable && sortColumn !== id) {
                                                e.target.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        {typeof header === 'function' ? header() : header}
                                        {renderSortIndicator(id, sortable)}
                                    </div>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onMouseDown={(e) => startResizing(id, index < orderedColumns.length - 1 ? orderedColumns[index + 1].id : null, e)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }}
                                        aria-label={`Resize column ${typeof header === 'function' ? id : header}`}
                                        style={{
                                            position: 'absolute',
                                            right: '-4px',
                                            top: 0,
                                            height: '100%',
                                            width: '8px',
                                            cursor: 'col-resize',
                                            userSelect: 'none',
                                            background: resizing?.columnId === id ? '#2aaae2' : 'transparent'
                                        }}
                                    />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(data || []).map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                onClick={() => onRowClick && onRowClick(row)}
                                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                className={getRowClassName ? getRowClassName(row) : ''}
                            >
                                {orderedColumns.map(({ id, cell }) => (
                                    <td
                                        key={id}
                                        style={{
                                            width: getColumnWidth(id),
                                            padding: '0.5rem',
                                            border: '1px solid var(--bs-table-border-color)'
                                        }}
                                    >
                                        {typeof cell === 'function' ? cell(row) : row[id]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ResizableTable; 